import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import TaskModal from '../components/TaskModal';
import { useTasks } from '../context/TaskContext';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const PRIORITY_COLORS = { High: '#ef4444', Medium: '#f59e0b', Low: '#10b981' };

const CalendarPage = () => {
  const { tasks } = useTasks();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
  const total = Math.ceil((firstDay + daysInMonth) / 7) * 7;

  const prev = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const next = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const getTasksForDate = (y, m, d) => {
    const ds = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return tasks.filter(t => t.deadline === ds);
  };

  const selectedTasks = selectedDay ? getTasksForDate(year, month, selectedDay) : [];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header search={search} setSearch={setSearch} onAddTask={() => setModal({ type: 'add' })} pageTitle="Calendar" />

      <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-4">
        {/* Nav */}
        <div className="flex items-center justify-between bg-white dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700/60 rounded-2xl px-5 py-3.5">
          <button onClick={prev} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors">
            <ChevronLeft size={16} />
          </button>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{MONTHS[month]} {year}</p>
            <p className="text-xs text-gray-400">{tasks.filter(t => t.deadline?.startsWith(`${year}-${String(month + 1).padStart(2, '0')}`)).length} tasks this month</p>
          </div>
          <button onClick={next} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors">
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Grid */}
        <div className="bg-white dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700/60 rounded-2xl overflow-hidden">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b border-gray-100 dark:border-gray-700">
            {DAYS.map(d => (
              <div key={d} className="py-2.5 text-center text-[11px] font-semibold text-gray-400 dark:text-gray-600 uppercase tracking-wide">{d}</div>
            ))}
          </div>
          {/* Days */}
          <div className="grid grid-cols-7">
            {Array.from({ length: total }, (_, i) => {
              let dayNum, curMonth = true;
              if (i < firstDay) { dayNum = prevDays - firstDay + i + 1; curMonth = false; }
              else if (i >= firstDay + daysInMonth) { dayNum = i - firstDay - daysInMonth + 1; curMonth = false; }
              else dayNum = i - firstDay + 1;

              const isToday = curMonth && dayNum === today.getDate() && month === today.getMonth() && year === today.getFullYear();
              const isSelected = curMonth && dayNum === selectedDay;
              const dayTasks = curMonth ? getTasksForDate(year, month, dayNum) : [];

              return (
                <div
                  key={i}
                  onClick={() => curMonth && setSelectedDay(dayNum === selectedDay ? null : dayNum)}
                  className={`min-h-[70px] p-1.5 border-b border-r border-gray-50 dark:border-gray-700/30 last:border-r-0 transition-colors ${curMonth ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-[#22263a]' : 'opacity-30'} ${isSelected ? 'bg-indigo-50 dark:bg-indigo-500/10' : ''}`}
                >
                  <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium mb-1 ${isToday ? 'bg-indigo-500 text-white' : isSelected ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400'}`}>
                    {dayNum}
                  </div>
                  <div className="space-y-0.5">
                    {dayTasks.slice(0, 2).map(t => (
                      <div
                        key={t.id}
                        className="text-[9px] px-1 py-0.5 rounded truncate font-medium"
                        style={{ background: PRIORITY_COLORS[t.priority] + '20', color: PRIORITY_COLORS[t.priority] }}
                      >
                        {t.title}
                      </div>
                    ))}
                    {dayTasks.length > 2 && <div className="text-[9px] text-gray-400 px-1">+{dayTasks.length - 2} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected day tasks */}
        {selectedDay && selectedTasks.length > 0 && (
          <div className="bg-white dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700/60 rounded-2xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
              Tasks for {MONTHS[month]} {selectedDay}
            </h3>
            <div className="space-y-2">
              {selectedTasks.map(t => (
                <div key={t.id} className="flex items-center gap-3 p-2.5 bg-gray-50 dark:bg-[#22263a] rounded-lg">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PRIORITY_COLORS[t.priority] }} />
                  <span className="text-sm text-gray-700 dark:text-gray-200 flex-1">{t.title}</span>
                  <span className="text-xs text-gray-400">{t.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {selectedDay && selectedTasks.length === 0 && (
          <div className="text-center py-6 text-sm text-gray-400">No tasks due on {MONTHS[month]} {selectedDay}</div>
        )}
      </div>

      {modal && <TaskModal modal={modal} onClose={() => setModal(null)} />}
    </div>
  );
};

export default CalendarPage;
